/**
 * Helpers for documentation export: ROQSTAR port order, VLAN u/t formatting.
 */

const ROQSTAR_PORT_ORDER: Record<string, number> = { G: 0, P: 1 };

function parseRoqstarPortId(raw: string): {
  groupRank: number;
  portNum: number;
  normalized: string;
} {
  const normalized = (raw ?? '').trim().toUpperCase();
  const match = /^([A-Z]+)\s*(\d+)$/.exec(normalized);
  if (!match)
    return { groupRank: 99, portNum: Number.MAX_SAFE_INTEGER, normalized };

  const prefix = match[1];
  const portNum = Number(match[2]);
  const groupRank = ROQSTAR_PORT_ORDER[prefix] ?? 50;
  return {
    groupRank,
    portNum: Number.isFinite(portNum) ? portNum : Number.MAX_SAFE_INTEGER,
    normalized,
  };
}

export const sortPortsROQSTAR = (a: string, b: string): number => {
  const pa = parseRoqstarPortId(a);
  const pb = parseRoqstarPortId(b);
  if (pa.groupRank !== pb.groupRank) return pa.groupRank - pb.groupRank;
  if (pa.portNum !== pb.portNum) return pa.portNum - pb.portNum;
  return pa.normalized.localeCompare(pb.normalized, undefined, {
    numeric: true,
  });
};
/**
 * Format VLAN IDs for export: sort by number, append "u" for untagged and "t" for tagged.
 */
export function formatVlanIdsWithUt(
  vlanIds: number[],
  untaggedVlan?: string,
): string {
  if (!vlanIds?.length) return '';
  const sorted = [...vlanIds].sort((a, b) => a - b);

  // Mirror configuration logic (see `configuration-vlan.ts`):
  // - untaggedVlan = "auto": if single VLAN => that VLAN is untagged, else all tagged
  // - untaggedVlan = "none": all tagged
  // - untaggedVlan = "<vid>": that VLAN is untagged *only if present in vlanIds*
  const mode = (untaggedVlan ?? 'auto').toString().trim().toLowerCase();
  let untaggedNum: number | undefined;
  if (mode === 'auto') {
    untaggedNum = sorted.length === 1 ? sorted[0] : undefined;
  } else if (mode === 'none' || mode === '') {
    untaggedNum = undefined;
  } else {
    const candidate = Number(untaggedVlan);
    untaggedNum =
      Number.isFinite(candidate) && sorted.includes(candidate)
        ? candidate
        : undefined;
  }

  return sorted
    .map((v) => `${v}${v === untaggedNum ? '(u)' : '(t)'}`)
    .join(', ');
}

/**
 * Sort VLAN IDs numerically and return as string (no u/t).
 */
export function formatVlanIdsSorted(vlanIds: number[]): string {
  if (!vlanIds?.length) return '';
  return [...vlanIds].sort((a, b) => a - b).join(', ');
}

/**
 * Uppercase every word in the string
 */
export function uppercaseEveryDashWord(str: string): string {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
